/**
 * Validates and sanitizes incoming HTML content from backend.
 * Ensures safe tags/attributes and basic structural requirements.
 *
 * @param {string} html - Raw HTML string to validate
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export function validateHtmlStructure(html) {
  if (typeof html !== "string") {
    return { isValid: false, error: "Invalid input: HTML must be a string." };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // ✅ Required structure selectors (blocking if missing)
  const mustHave = [
    ".flex.justify-between", // header section
    ".border-t",             // metadata section (timestamp/source)
  ];

  // ⚠️ Optional sections (log warning but allow)
  const optionalSections = [".bg-gray-50"]; // scorecard container

  // ❌ Forbidden HTML tags (XSS and injection risks)
  const forbiddenTags = ["script", "iframe", "object", "embed"];

  // 🔐 Check for required sections
  for (const selector of mustHave) {
    if (!doc.querySelector(selector)) {
      return {
        isValid: false,
        error: `Missing required HTML section: ${selector}`,
      };
    }
  }

  // ⚠️ Log missing optional parts (but don't block)
  for (const selector of optionalSections) {
    if (!doc.querySelector(selector)) {
      console.warn(`Optional section missing: ${selector}`);
    }
  }

  // ❌ Block dangerous tags
  for (const tag of forbiddenTags) {
    if (doc.querySelector(tag)) {
      return {
        isValid: false,
        error: `Forbidden tag found: <${tag}>`,
      };
    }
  }

  // ❌ Block unsafe attributes (e.g., onclick, onerror)
  const hasUnsafeAttrs = [...doc.querySelectorAll("*")].some((el) =>
    [...el.attributes].some((attr) =>
      attr.name.toLowerCase().startsWith("on")
    )
  );

  if (hasUnsafeAttrs) {
    return {
      isValid: false,
      error: "Unsafe attribute found (e.g., onclick, onerror, etc.)",
    };
  }

  return { isValid: true, error: null };
}
